import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  FolderOpen, Briefcase, Receipt, Upload, Trash2,
  FileText, Plus, Camera, ChevronDown, ChevronRight, ArrowLeft,
  X, Share2, Eye
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import DateInput from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { cn } from "../../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MieiDocumenti = () => {
  const navigate = useNavigate();
  const { token, getAuthHeaders, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ documenti: [], contratti: [], buste_paga: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ nome: "", data_documento: "", data_scadenza: "", note: "", file: null, file_name: "" });
  const [expandedSection, setExpandedSection] = useState("documenti");
  const [preview, setPreview] = useState(null); // { url, fileName, isImage }

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/miei-documenti`, { headers: getAuthHeaders() });
      setData(res.data);
      axios.post(`${API}/miei-documenti/notifiche/read`, {}, { headers: getAuthHeaders() }).catch(() => {});
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getFileUrl = (filePathRaw) => {
    // Lazy: import sincrono via require non funziona con CRA; usiamo helper inline
    const token = (window.localStorage && window.localStorage.getItem("token")) || "";
    const base = `${process.env.REACT_APP_BACKEND_URL || ""}${filePathRaw || ""}`;
    if (!token) return base;
    const sep = base.includes("?") ? "&" : "?";
    return `${base}${sep}t=${encodeURIComponent(token)}`;
  };

  const isImageFile = (fileName) => /\.(jpg|jpeg|png|gif|webp|heic|heif)$/i.test(fileName || '');

  const openPreview = (fileUrl, fileName) => {
    if (!fileUrl) return;
    setPreview({ url: getFileUrl(fileUrl), fileName, isImage: isImageFile(fileName) });
  };

  const handleShare = async () => {
    if (!preview) return;
    try {
      const res = await fetch(preview.url);
      const blob = await res.blob();
      const file = new File([blob], preview.fileName, { type: blob.type });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: preview.fileName });
        return;
      }
      // Fallback senza file
      await navigator.share({ url: preview.url, title: preview.fileName });
    } catch (e) {
      // AbortError = utente ha chiuso lo share sheet, non è un errore
      if (e?.name === 'AbortError') return;
      // Fallback: download diretto
      const a = document.createElement('a');
      a.href = preview.url;
      a.download = preview.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success("Download avviato");
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("File troppo grande (max 10MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { setForm(prev => ({ ...prev, file: reader.result, file_name: file.name })); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleSubmit = async () => {
    if (!form.nome.trim()) { toast.error("Inserisci il nome del documento"); return; }
    setUploading(true);
    try {
      await axios.post(`${API}/miei-documenti`, {
        nome: form.nome,
        data_documento: form.data_documento || null,
        data_scadenza: form.data_scadenza || null,
        note: form.note || null,
        file_data: form.file,
        file_name: form.file_name
      }, { headers: getAuthHeaders() });
      toast.success("Documento caricato");
      setDialogOpen(false);
      setForm({ nome: "", data_documento: "", data_scadenza: "", note: "", file: null, file_name: "" });
      fetchData();
    } catch {
      toast.error("Errore nel caricamento");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await axios.delete(`${API}/miei-documenti/${docId}`, { headers: getAuthHeaders() });
      toast.success("Documento eliminato");
      fetchData();
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore nell'eliminazione";
      toast.error(msg);
    }
  };

  const sections = [
    {
      key: "documenti",
      label: "Documenti",
      icon: FolderOpen,
      items: data.documenti,
      canDelete: true,
      renderItem: (doc) => ({
        id: doc.id,
        fileUrl: doc.file_url,
        fileName: doc.nome + (doc.file_url ? doc.file_url.substring(doc.file_url.lastIndexOf('.')) : ''),
        title: doc.nome,
        subtitle: doc.data_documento ? new Date(doc.data_documento).toLocaleDateString("it-IT") : null,
        badge: doc.data_scadenza && new Date(doc.data_scadenza) < new Date() ? "Scaduto" : null,
        canDelete: doc.uploaded_by === user?.id || doc.uploaded_by_role === "operatore"
      })
    },
    {
      key: "contratti",
      label: "Contratti",
      icon: Briefcase,
      items: data.contratti,
      canDelete: false,
      renderItem: (c) => ({
        id: c.id,
        fileUrl: c.file_url,
        fileName: c.file_nome || c.nome,
        title: c.nome || c.file_nome,
        subtitle: [
          c.data_inizio && new Date(c.data_inizio).toLocaleDateString("it-IT"),
          c.data_scadenza && new Date(c.data_scadenza).toLocaleDateString("it-IT")
        ].filter(Boolean).join(" → ") || null,
        canDelete: false
      })
    },
    {
      key: "buste_paga",
      label: "Buste Paga",
      icon: Receipt,
      items: data.buste_paga,
      canDelete: false,
      renderItem: (b) => ({
        id: b.id,
        fileUrl: b.file_url,
        fileName: b.file_nome || `Busta_${b.mese_label}_${b.anno}.pdf`,
        title: `${b.mese_label} ${b.anno}`,
        subtitle: b.file_nome,
        canDelete: false
      })
    }
  ];

  return (
    <div className="min-h-screen" style={{ background: "#f0f0f5" }} data-testid="miei-documenti-page">
      {/* Header */}
      <header className="sticky top-0 z-20 px-4 py-3 flex items-center gap-3" style={{ background: "rgba(240,240,245,0.95)", backdropFilter: "blur(12px)" }}>
        <button onClick={() => navigate("/operatore")} className="w-10 h-10 rounded-2xl bg-white/80 flex items-center justify-center shadow-sm active:scale-95" data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" style={{ color: "#6C63FF" }} />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold" style={{ color: "#1f2937" }}>I miei Documenti</h1>
          <p className="text-xs" style={{ color: "#9ca3af" }}>{user?.nome || user?.email || user?.telefono}</p>
        </div>
        <button
          onClick={() => setDialogOpen(true)}
          className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-md active:scale-95"
          style={{ background: "linear-gradient(135deg, #6C63FF, #8B85FF)" }}
          data-testid="add-documento-btn"
        >
          <Plus className="h-5 w-5 text-white" />
        </button>
      </header>

      <main className="max-w-lg mx-auto px-4 pb-8">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "#6C63FF" }} />
          </div>
        ) : (
          <div className="space-y-3 mt-2">
            {sections.map(section => {
              const isOpen = expandedSection === section.key;
              const Icon = section.icon;
              return (
                <div key={section.key} className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`section-${section.key}`}>
                  <button
                    className="w-full flex items-center gap-3 p-4 active:bg-gray-50 text-left"
                    onClick={() => setExpandedSection(isOpen ? null : section.key)}
                  >
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: "rgba(108,99,255,0.1)" }}>
                      <Icon className="h-5 w-5" style={{ color: "#6C63FF" }} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-sm" style={{ color: "#1f2937" }}>{section.label}</p>
                      <p className="text-xs" style={{ color: "#9ca3af" }}>{section.items.length} elementi</p>
                    </div>
                    {isOpen
                      ? <ChevronDown className="h-4 w-4" style={{ color: "#9ca3af" }} />
                      : <ChevronRight className="h-4 w-4" style={{ color: "#9ca3af" }} />
                    }
                  </button>

                  {isOpen && (
                    <div className="border-t" style={{ borderColor: "#f3f4f6" }}>
                      {section.items.length === 0 ? (
                        <div className="p-6 text-center">
                          <FileText className="h-8 w-8 mx-auto mb-2" style={{ color: "#d1d5db" }} />
                          <p className="text-xs" style={{ color: "#9ca3af" }}>Nessun elemento</p>
                        </div>
                      ) : (
                        <div className="divide-y" style={{ borderColor: "#f9fafb" }}>
                          {section.items.map(item => {
                            const r = section.renderItem(item);
                            return (
                              <div key={r.id} className="flex items-center gap-3 px-4 py-3" data-testid={`item-${r.id}`}>
                                {r.fileUrl ? (
                                  <button
                                    onClick={() => openPreview(r.fileUrl, r.fileName)}
                                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 shadow-sm active:scale-95"
                                    style={{ background: "#6C63FF" }}
                                    data-testid={`view-${r.id}`}
                                  >
                                    <Eye className="h-5 w-5 text-white" />
                                  </button>
                                ) : (
                                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#f3f4f6" }}>
                                    <FileText className="h-5 w-5" style={{ color: "#9ca3af" }} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate" style={{ color: "#1f2937" }}>{r.title}</p>
                                  {r.subtitle && <p className="text-xs" style={{ color: "#9ca3af" }}>{r.subtitle}</p>}
                                  {r.badge && <Badge className="bg-red-500 text-white text-[9px] px-1 py-0 mt-0.5">{r.badge}</Badge>}
                                </div>
                                {r.canDelete && (
                                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-red-500 hover:bg-red-50" onClick={() => handleDelete(r.id)} data-testid={`delete-${r.id}`}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Preview overlay - viewer inline */}
      {preview && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "#000" }} data-testid="preview-overlay">
          {/* Top bar */}
          <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }}>
            <button
              onClick={() => setPreview(null)}
              className="w-10 h-10 rounded-2xl flex items-center justify-center active:scale-95"
              style={{ background: "#fff" }}
              data-testid="close-preview-btn"
            >
              <X className="h-5 w-5" style={{ color: "#1f2937" }} />
            </button>
            <p className="flex-1 text-sm font-semibold truncate" style={{ color: "#fff" }}>{preview.fileName}</p>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto flex items-center justify-center">
            {preview.isImage ? (
              <img
                src={preview.url}
                alt={preview.fileName}
                className="max-w-full max-h-full object-contain"
                style={{ touchAction: "pinch-zoom" }}
                data-testid="preview-image"
              />
            ) : (
              <iframe
                src={preview.url}
                title={preview.fileName}
                className="w-full h-full border-0"
                style={{ background: "#fff" }}
                data-testid="preview-iframe"
              />
            )}
          </div>

          {/* Bottom action bar */}
          <div className="shrink-0 px-4 py-5 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)" }}>
            <button
              onClick={handleShare}
              className="flex items-center gap-2.5 px-8 py-3 rounded-2xl active:scale-95 transition-transform"
              style={{ background: "#fff" }}
              data-testid="share-btn"
            >
              <Share2 className="h-5 w-5" style={{ color: "#6C63FF" }} />
              <span className="text-sm font-semibold" style={{ color: "#1f2937" }}>Condividi</span>
            </button>
          </div>
        </div>
      )}

      {/* Dialog nuovo documento */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5" style={{ color: "#6C63FF" }} />
              Carica Documento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Nome Documento *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="es. Carta d'identità, Codice fiscale..." data-testid="doc-nome-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data Documento</Label>
                <DateInput value={form.data_documento} onChange={(v) => setForm({ ...form, data_documento: v })} />
              </div>
              <div>
                <Label>Scadenza</Label>
                <DateInput value={form.data_scadenza} onChange={(v) => setForm({ ...form, data_scadenza: v })} />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note opzionali..." />
            </div>
            <div>
              <Label>Allegato</Label>
              <div className="mt-2 space-y-2">
                {form.file_name ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl border" style={{ background: "rgba(108,99,255,0.05)", borderColor: "rgba(108,99,255,0.2)" }}>
                    <FileText className="h-5 w-5 shrink-0" style={{ color: "#6C63FF" }} />
                    <span className="flex-1 text-sm font-medium truncate">{form.file_name}</span>
                    <button onClick={() => setForm({ ...form, file: null, file_name: "" })} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 transition-colors" style={{ borderColor: "#d1d5db" }}>
                      <Upload className="h-6 w-6 mb-1" style={{ color: "#9ca3af" }} />
                      <span className="text-xs font-medium" style={{ color: "#6b7280" }}>Scegli file</span>
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*" onChange={handleFileSelect} data-testid="file-input" />
                    </label>
                    <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed rounded-xl cursor-pointer hover:border-primary/50 transition-colors" style={{ borderColor: "#d1d5db" }}>
                      <Camera className="h-6 w-6 mb-1" style={{ color: "#9ca3af" }} />
                      <span className="text-xs font-medium" style={{ color: "#6b7280" }}>Scatta foto</span>
                      <input type="file" className="hidden" accept="image/*" capture="environment" onChange={handleFileSelect} data-testid="camera-input" />
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSubmit} disabled={uploading} data-testid="submit-doc-btn">
              {uploading ? "Caricamento..." : "Carica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MieiDocumenti;
